import { useState, useMemo, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { MediaUri, productStatusOptions } from "../../../constants/enum";
import { getClusterPriority, getSeriesPriority } from "../config/seriesPriority";

// Types
export interface Product {
  id: string;
  brand: string;
  brandType: string;
  materialCode: string;
  materialName?: string;
  barcode: string;
  name: string;
  titleEN: string;
  titleTH: string;
  category: string;
  subcategory?: string;
  collection?: string;
  color?: string;
  packaging?: string;
  status?: string;
  description?: string;
  descriptionEN?: string;
  descriptionTH?: string;
  images?: string;
  productHierachy?: string;
  [key: string]: any;
}

interface SerieGroup {
  name: string;
  count: number;
  products: Product[];
}

interface SerieGroupCluster {
  key: string;
  series: Record<string, SerieGroup>;
  totalCount: number;
}

interface HierarchyNode {
  name: string;
  count: number;
  children?: Record<string, HierarchyNode>;
  products?: Product[];
  series?: Record<string, SerieGroup>;
}

interface FolderViewProps {
  products: Product[];
  themeColors: { bg: string; border: string; text: string };
  searchTerm: string;
  setSearchTerm: (term: string) => void;
}

// Helper function to get serie key from productHierachy (first 13 characters)
const getSerieKey = (productHierachy: string | undefined): string => {
  if (!productHierachy || productHierachy.length < 13) return "Unknown Serie";
  return productHierachy.substring(0, 13);
};

// Helper function to extract product name prefix (chars before first number)
const getProductNamePrefix = (name: string | undefined): string => {
  if (!name) return "Unknown";
  const match = name.match(/^([^\d]+)/);
  return match ? match[1].trim() : "Unknown";
};

// Helper function to get last character of serie key for sorting
const getSerieLastChar = (serieKey: string): string => {
  if (!serieKey || serieKey.length === 0) return "";
  return serieKey.charAt(serieKey.length - 1);
};

// Helper function to extract size number from product name (first number after name prefix)
const getProductSize = (name: string | undefined): number => {
  if (!name) return Infinity;
  // Remove the name prefix (text before first number)
  const match = name.match(/^[^\d]*(\d+)/);
  if (match && match[1]) {
    return parseInt(match[1], 10);
  }
  return Infinity;
};

// Helper function to group series by product name prefix (merge same names)
// Then within each cluster, group by color (last character) and merge products
const groupSeriesByClusters = (series: Record<string, SerieGroup>): SerieGroupCluster[] => {
  const clusterMap: Record<string, SerieGroupCluster> = {};

  Object.entries(series).forEach(([serieKey, serieGroup]) => {
    const firstProduct = serieGroup.products[0];
    const namePrefix = getProductNamePrefix(firstProduct?.name);
    const clusterKey = namePrefix;
    const colorKey = getSerieLastChar(serieKey); // Group by color (last char)

    if (!clusterMap[clusterKey]) {
      clusterMap[clusterKey] = {
        key: clusterKey,
        series: {},
        totalCount: 0,
      };
    }

    // Merge series with same color into one group
    if (!clusterMap[clusterKey].series[colorKey]) {
      clusterMap[clusterKey].series[colorKey] = {
        name: colorKey,
        count: 0,
        products: [],
      };
    }

    clusterMap[clusterKey].series[colorKey].count += serieGroup.count;
    clusterMap[clusterKey].series[colorKey].products.push(...serieGroup.products);
    clusterMap[clusterKey].totalCount += serieGroup.count;
  });

  // Sort clusters by priority, sort series (colors) by priority within each cluster, and sort products by size within each color
  return Object.values(clusterMap)
    .map((cluster) => ({
      ...cluster,
      series: Object.fromEntries(
        Object.entries(cluster.series)
          .sort(([a], [b]) => getSeriesPriority(a, cluster.key) - getSeriesPriority(b, cluster.key))
          .map(([colorKey, serieGroup]) => [
            colorKey,
            {
              ...serieGroup,
              products: [...serieGroup.products].sort(
                (a, b) => getProductSize(a.name) - getProductSize(b.name)
              ),
            },
          ])
      ),
    }))
    .sort((a, b) => getClusterPriority(a.key) - getClusterPriority(b.key));
};

// Helper function to build hierarchy from flat array
const buildHierarchy = (products: Product[]): Record<string, HierarchyNode> => {
  const hierarchy: Record<string, HierarchyNode> = {};

  products.forEach((product) => {
    const category = product.category || "Uncategorized";
    const subcategory = product.subcategory || "General";
    const brandType = product.brandType || "Standard";
    const serieKey = getSerieKey(product.productHierachy);

    if (!hierarchy[category]) {
      hierarchy[category] = { name: category, count: 0, children: {} };
    }
    hierarchy[category].count++;

    if (!hierarchy[category].children![subcategory]) {
      hierarchy[category].children![subcategory] = { name: subcategory, count: 0, children: {} };
    }
    hierarchy[category].children![subcategory].count++;

    if (!hierarchy[category].children![subcategory].children![brandType]) {
      hierarchy[category].children![subcategory].children![brandType] = {
        name: brandType,
        count: 0,
        series: {},
      };
    }
    hierarchy[category].children![subcategory].children![brandType].count++;

    if (!hierarchy[category].children![subcategory].children![brandType].series![serieKey]) {
      hierarchy[category].children![subcategory].children![brandType].series![serieKey] = {
        name: serieKey,
        count: 0,
        products: [],
      };
    }
    hierarchy[category].children![subcategory].children![brandType].series![serieKey].count++;
    hierarchy[category].children![subcategory].children![brandType].series![serieKey].products.push(product);
  });

  return hierarchy;
};

// Helper function to get status label from productStatusOptions
const getStatusLabel = (statusValue: string | undefined): string | null => {
  if (!statusValue) return null;
  const status = productStatusOptions.find((opt) => opt.value === statusValue);
  return status ? status.label : null;
};

// Status badge color mapping
const statusColors: Record<string, { bg: string; text: string }> = {
  "01": { bg: "#DBEAFE", text: "#1E40AF" },
  "02": { bg: "#D1FAE5", text: "#065F46" },
  "03": { bg: "#FEF3C7", text: "#92400E" },
  "04": { bg: "#E5E7EB", text: "#374151" },
  "05": { bg: "#FEE2E2", text: "#991B1B" },
  "06": { bg: "#F3E8FF", text: "#6B21A8" },
};

// Detect if device is touch-based (mobile/tablet)
const isTouchDevice = () => {
  if (typeof window === "undefined") return false;
  return "ontouchstart" in window || navigator.maxTouchPoints > 0;
};

// Product Card Component - Responsive
const ProductCard = ({ product, themeColors }: { product: Product; themeColors: { bg: string; border: string; text: string } }) => {
  const images = product.images ? JSON.parse(product.images) : null;
  const firstImage = Array.isArray(images) && images.length > 0 ? images[0] : null;
  const imageUrl = firstImage ? `${MediaUri}/display/${firstImage}` : null;
  const [imageError, setImageError] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [tooltipPlacement, setTooltipPlacement] = useState<"top" | "bottom" | "left" | "right">("top");
  const cardRef = useRef<HTMLDivElement>(null);

  const statusLabel = getStatusLabel(product.status);
  const statusColor = product.status ? statusColors[product.status] : null;

  const handleClick = () => {
    const productUrl = `/product/${product.barcode}_${product.materialCode}/show`;
    window.open(productUrl, "_blank");
  };

  const handleMouseEnter = () => {
    // Don't show tooltip on touch devices
    if (isTouchDevice()) return;

    if (cardRef.current) {
      const rect = cardRef.current.getBoundingClientRect();
      const tooltipWidth = 256; // w-64 = 16rem = 256px
      const tooltipHeight = 200; // estimated height
      const margin = 4; // reduced margin for closer positioning

      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      let top: number;
      let left: number;

      // Center tooltip horizontally relative to card
      left = rect.left + (rect.width / 2) - (tooltipWidth / 2);

      // Ensure tooltip doesn't go off screen horizontally
      if (left < margin) {
        left = margin;
      } else if (left + tooltipWidth > viewportWidth - margin) {
        left = viewportWidth - tooltipWidth - margin;
      }

      // Priority: Top first, then Bottom, then Right, then Left
      let placement: "top" | "bottom" | "left" | "right" = "top";

      if (rect.top - margin - tooltipHeight > 0) {
        // Place above
        top = (rect.top - tooltipHeight - margin) * 1.1;
        placement = "top";
      } else if (rect.bottom + margin + tooltipHeight < viewportHeight) {
        // Place below
        top = rect.bottom + margin;
        placement = "bottom";
      } else if (rect.right + margin + tooltipWidth < viewportWidth) {
        // Place right
        top = rect.top;
        left = rect.right + margin;
        placement = "right";
      } else if (rect.left - margin - tooltipWidth > 0) {
        // Place left
        top = rect.top;
        left = rect.left - margin - tooltipWidth;
        placement = "left";
      } else {
        // Default: place above and adjust
        top = margin;
        placement = "top";
      }

      setTooltipPosition({ top, left });
      setTooltipPlacement(placement);
    }
    setShowTooltip(true);
  };

  // Check if there's any content to show in tooltip
  const hasTooltipContent = product.titleEN || product.titleTH || product.description || product.descriptionEN || product.descriptionTH;

  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      ref={cardRef}
      className="relative flex flex-col items-center p-1.5 sm:p-2 transition-all border border-gray-100 rounded-lg shadow-sm cursor-pointer hover:shadow-md active:scale-95"
      style={{
        width: "clamp(100px, 28vw, 127px)",
        backgroundColor: isHovered ? themeColors.bg : "white",
      }}
      onClick={handleClick}
      onMouseEnter={() => {
        handleMouseEnter();
        setIsHovered(true);
      }}
      onMouseLeave={() => {
        setShowTooltip(false);
        setIsHovered(false);
      }}
    >
      {/* Tooltip using Portal */}
      {showTooltip && hasTooltipContent && createPortal(
        <div
          className="fixed z-[9999] w-64 p-3 text-left bg-gray-900 rounded-lg shadow-xl text-white text-[11px] leading-relaxed"
          style={{ top: tooltipPosition.top, left: tooltipPosition.left }}
        >
          {/* Arrow - position based on tooltip placement */}
          {tooltipPlacement === "top" && (
            <div className="absolute w-2 h-2 rotate-45 -translate-x-1/2 bg-gray-900 left-1/2 -bottom-1" />
          )}
          {tooltipPlacement === "bottom" && (
            <div className="absolute w-2 h-2 rotate-45 -translate-x-1/2 bg-gray-900 left-1/2 -top-1" />
          )}
          {tooltipPlacement === "right" && (
            <div className="absolute w-2 h-2 rotate-45 bg-gray-900 -left-1 top-4" />
          )}
          {tooltipPlacement === "left" && (
            <div className="absolute w-2 h-2 rotate-45 bg-gray-900 -right-1 top-4" />
          )}

          {product.titleEN && (
            <div className="mb-2">
              <span className="font-semibold text-gray-300">Title EN:</span>
              <p className="mt-0.5">{product.titleEN}</p>
            </div>
          )}
          {product.titleTH && (
            <div className="mb-2">
              <span className="font-semibold text-gray-300">Title TH:</span>
              <p className="mt-0.5">{product.titleTH}</p>
            </div>
          )}
          {product.description && (
            <div className="mb-2">
              <span className="font-semibold text-gray-300">Description:</span>
              <p className="mt-0.5">{product.description}</p>
            </div>
          )}
          {product.descriptionEN && (
            <div className="mb-2">
              <span className="font-semibold text-gray-300">Desc EN:</span>
              <p className="mt-0.5">{product.descriptionEN}</p>
            </div>
          )}
          {product.descriptionTH && (
            <div>
              <span className="font-semibold text-gray-300">Desc TH:</span>
              <p className="mt-0.5">{product.descriptionTH}</p>
            </div>
          )}
        </div>,
        document.body
      )}

      {statusLabel && statusColor && (
        <div
          className="absolute top-0.5 right-0.5 sm:top-1 sm:right-1 px-1 sm:px-1.5 py-0.5 text-[8px] sm:text-[10px] font-medium rounded"
          style={{ backgroundColor: statusColor.bg, color: statusColor.text }}
        >
          {statusLabel}
        </div>
      )}
      <div className="flex items-center justify-center w-full h-16 mb-1 rounded sm:h-20 sm:mb-2 bg-gray-50">
        {imageUrl && !imageError ? (
          <img
            src={imageUrl}
            alt={product.name || "Product"}
            className="object-contain w-full h-full"
            loading="lazy"
            onError={() => setImageError(true)}
          />
        ) : (
          <div className="flex items-center justify-center w-full h-full text-xs text-gray-400">
            <svg className="w-6 h-6 sm:w-8 sm:h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
        )}
      </div>
      <div className="flex flex-col justify-between flex-1 w-full text-center">
        <p className="h-7 text-[10px] sm:h-8 sm:text-xs font-medium leading-tight text-gray-700 line-clamp-2">
          {product.name || product.materialName || "N/A"}
        </p>
        <p
          className="mt-0.5 sm:mt-1 text-[9px] sm:text-xs font-mono px-1 py-0.5 rounded truncate"
          style={{ backgroundColor: themeColors.bg, color: themeColors.text }}
        >
          {product.materialCode}
        </p>
      </div>
    </div>
  );
};

// Accordion Item Component - Responsive
const AccordionItem = ({
  title,
  count,
  level,
  isExpanded,
  onToggle,
  themeColors,
  children,
}: {
  title: string;
  count: number;
  level: number;
  isExpanded: boolean;
  onToggle: () => void;
  themeColors: { bg: string; border: string; text: string };
  children: React.ReactNode;
}) => {
  // Responsive padding - smaller on mobile
  const basePadding = typeof window !== "undefined" && window.innerWidth < 640 ? 12 : 20;
  const paddingLeft = level * basePadding;

  return (
    <div className={`border-b border-gray-100 last:border-b-0 ${level === 0 ? "" : ""}`}>
      <button
        onClick={onToggle}
        className={`w-full flex items-center justify-between py-2 sm:py-3 px-2 sm:px-4 text-left transition-colors active:bg-gray-100 ${
          level === 0 ? "hover:bg-gray-50" : "hover:bg-gray-50/50"
        }`}
        style={{ paddingLeft: paddingLeft + (typeof window !== "undefined" && window.innerWidth < 640 ? 8 : 16) }}
      >
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          <svg
            className={`w-3.5 h-3.5 sm:w-4 sm:h-4 transition-transform duration-200 flex-shrink-0 ${isExpanded ? "rotate-90" : ""}`}
            style={{ color: themeColors.border }}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          <span
            className={`font-medium truncate ${level === 0 ? "text-sm sm:text-base" : "text-xs sm:text-sm"}`}
            style={{ color: level === 0 ? themeColors.text : "#374151" }}
          >
            {title}
          </span>
        </div>
        <span
          className={`px-1.5 sm:px-2.5 py-0.5 text-[10px] sm:text-xs font-semibold rounded-full flex-shrink-0 ml-2 ${
            level === 0 ? "" : level === 1 ? "opacity-80" : "opacity-60"
          }`}
          style={{
            backgroundColor: themeColors.bg,
            color: themeColors.text,
          }}
        >
          {count.toLocaleString()}
        </span>
      </button>
      {isExpanded && (
        <div className="pb-1 sm:pb-2">{children}</div>
      )}
    </div>
  );
};

function FolderView({ products, themeColors, searchTerm, setSearchTerm }: FolderViewProps) {
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());

  // Build hierarchy from products
  const hierarchy = useMemo(() => buildHierarchy(products), [products]);

  // Toggle accordion node
  const toggleNode = useCallback((nodeKey: string) => {
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(nodeKey)) {
        next.delete(nodeKey);
      } else {
        next.add(nodeKey);
      }
      return next;
    });
  }, []);

  // Expand all categories
  const expandAll = useCallback(() => {
    const allKeys = new Set<string>();
    Object.entries(hierarchy).forEach(([catKey, catNode]) => {
      allKeys.add(catKey);
      if (catNode.children) {
        Object.entries(catNode.children).forEach(([subKey, subNode]) => {
          allKeys.add(`${catKey}|${subKey}`);
          if (subNode.children) {
            Object.keys(subNode.children).forEach((btKey) => {
              allKeys.add(`${catKey}|${subKey}|${btKey}`);
            });
          }
        });
      }
    });
    setExpandedNodes(allKeys);
  }, [hierarchy]);

  // Collapse all
  const collapseAll = useCallback(() => {
    setExpandedNodes(new Set());
  }, []);

  // Summary stats
  const stats = useMemo(() => {
    const categories = Object.keys(hierarchy).length;
    let subcategories = 0;
    let brandTypes = 0;
    let series = 0;

    Object.values(hierarchy).forEach((cat) => {
      if (cat.children) {
        subcategories += Object.keys(cat.children).length;
        Object.values(cat.children).forEach((sub) => {
          if (sub.children) {
            brandTypes += Object.keys(sub.children).length;
            Object.values(sub.children).forEach((bt) => {
              if (bt.series) {
                series += Object.keys(bt.series).length;
              }
            });
          }
        });
      }
    });

    return { categories, subcategories, brandTypes, series, products: products.length };
  }, [hierarchy, products]);

  return (
    <div className="mt-2 sm:mt-4">
      {/* Search and Stats Bar - Responsive */}
      <div className="p-2 mb-2 bg-white rounded-lg shadow sm:p-4 sm:mb-4">
        <div className="flex flex-col gap-2 sm:gap-4">
          {/* Top row: Search + Buttons */}
          <div className="flex flex-col gap-2 sm:gap-4 sm:flex-row sm:items-center">
            {/* Search Input */}
            <div className="relative flex-1">
              <svg
                className="absolute w-4 h-4 text-gray-400 transform -translate-y-1/2 sm:w-5 sm:h-5 left-2 sm:left-3 top-1/2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <input
                type="text"
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full py-2 pl-8 pr-8 text-xs border border-gray-300 rounded-lg sm:pl-10 sm:pr-10 sm:text-sm focus:outline-none focus:ring-2"
                style={{ "--tw-ring-color": themeColors.border } as React.CSSProperties}
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm("")}
                  className="absolute text-gray-400 transform -translate-y-1/2 right-2 sm:right-3 top-1/2 hover:text-gray-600"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>

            {/* Expand/Collapse Buttons */}
            <div className="flex flex-shrink-0 gap-2">
              <button
                onClick={expandAll}
                className="flex-1 px-2 py-1.5 text-xs transition-colors border rounded-lg sm:flex-none sm:px-3 sm:py-2 sm:text-sm hover:bg-gray-50 active:bg-gray-100 whitespace-nowrap"
                style={{ borderColor: themeColors.border, color: themeColors.text }}
              >
                Expand All
              </button>
              <button
                onClick={collapseAll}
                className="flex-1 px-2 py-1.5 text-xs text-gray-600 transition-colors border border-gray-300 rounded-lg sm:flex-none sm:px-3 sm:py-2 sm:text-sm hover:bg-gray-50 active:bg-gray-100 whitespace-nowrap"
              >
                Collapse All
              </button>
            </div>
          </div>

          {/* Bottom row: Stats - scrollable on mobile */}
          <div className="flex items-center gap-1.5 sm:gap-2 text-[10px] sm:text-sm overflow-x-auto pb-1 sm:pb-0 sm:flex-wrap">
            <span className="flex-shrink-0 px-1.5 sm:px-2 py-0.5 sm:py-1 text-gray-600 bg-gray-100 rounded">
              <strong>{stats.categories}</strong> <span className="hidden xs:inline">Categories</span><span className="xs:hidden">Cat</span>
            </span>
            <span className="flex-shrink-0 px-1.5 sm:px-2 py-0.5 sm:py-1 text-gray-600 bg-gray-100 rounded">
              <strong>{stats.subcategories}</strong> <span className="hidden xs:inline">Sub-categories</span><span className="xs:hidden">Sub</span>
            </span>
            <span className="flex-shrink-0 px-1.5 sm:px-2 py-0.5 sm:py-1 text-gray-600 bg-gray-100 rounded">
              <strong>{stats.brandTypes}</strong> <span className="hidden sm:inline">Brand Types</span><span className="sm:hidden">Types</span>
            </span>
            <span className="flex-shrink-0 px-1.5 sm:px-2 py-0.5 sm:py-1 text-gray-600 bg-gray-100 rounded">
              <strong>{stats.series}</strong> Series
            </span>
            <span
              className="flex-shrink-0 px-1.5 sm:px-2 py-0.5 sm:py-1 font-medium rounded"
              style={{ backgroundColor: themeColors.bg, color: themeColors.text }}
            >
              <strong>{stats.products.toLocaleString()}</strong> Products
            </span>
          </div>
        </div>
      </div>

      {/* Hierarchy Accordion */}
      <div className="overflow-hidden bg-white rounded-lg shadow">
        {Object.entries(hierarchy)
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([categoryKey, categoryNode]) => (
            <AccordionItem
              key={categoryKey}
              title={categoryKey}
              count={categoryNode.count}
              level={0}
              isExpanded={expandedNodes.has(categoryKey)}
              onToggle={() => toggleNode(categoryKey)}
              themeColors={themeColors}
            >
              {categoryNode.children &&
                Object.entries(categoryNode.children)
                  .sort(([a], [b]) => a.localeCompare(b))
                  .map(([subKey, subNode]) => {
                    const subNodeKey = `${categoryKey}|${subKey}`;
                    return (
                      <AccordionItem
                        key={subNodeKey}
                        title={subKey}
                        count={subNode.count}
                        level={1}
                        isExpanded={expandedNodes.has(subNodeKey)}
                        onToggle={() => toggleNode(subNodeKey)}
                        themeColors={themeColors}
                      >
                        {subNode.children &&
                          Object.entries(subNode.children)
                            .sort(([a], [b]) => a.localeCompare(b))
                            .map(([btKey, btNode]) => {
                              const btNodeKey = `${categoryKey}|${subKey}|${btKey}`;
                              return (
                                <AccordionItem
                                  key={btNodeKey}
                                  title={btKey}
                                  count={btNode.count}
                                  level={2}
                                  isExpanded={expandedNodes.has(btNodeKey)}
                                  onToggle={() => toggleNode(btNodeKey)}
                                  themeColors={themeColors}
                                >
                                  {/* Series grouped by clusters - responsive inline display */}
                                  {btNode.series && (
                                    <div className="px-2 py-2 ml-4 sm:px-4 sm:py-4 sm:ml-12 md:ml-16">
                                      <div className="flex flex-wrap items-start gap-2 sm:gap-3">
                                        {groupSeriesByClusters(btNode.series).map((cluster, clusterIndex) => (
                                          <div
                                            key={cluster.key}
                                            className="inline-flex flex-col w-full p-2 border-2 border-solid sm:w-auto sm:p-3 rounded-xl"
                                            style={{ borderColor: themeColors.text, backgroundColor: `${themeColors.bg}30` }}
                                          >
                                            {/* Cluster Header */}
                                            <div className="flex items-center justify-between gap-2 pb-1.5 sm:pb-2 mb-1.5 sm:mb-2 border-b" style={{ borderColor: themeColors.border }}>
                                              <div className="flex items-center gap-1.5 sm:gap-2">
                                                <span
                                                  className="w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center text-[9px] sm:text-[10px] font-bold rounded-full bg-gray-600 text-white"
                                                >
                                                  {clusterIndex + 1}
                                                </span>
                                                <span
                                                  className="px-1.5 sm:px-2 py-0.5 text-[9px] sm:text-[10px] font-bold rounded"
                                                  style={{ backgroundColor: themeColors.text, color: "white" }}
                                                >
                                                  {cluster.key || "Unknown"}
                                                </span>
                                              </div>
                                              <span
                                                className="px-1.5 sm:px-2 py-0.5 text-[9px] sm:text-[10px] font-semibold rounded"
                                                style={{ backgroundColor: themeColors.bg, color: themeColors.text }}
                                              >
                                                {cluster.totalCount}
                                              </span>
                                            </div>
                                            {/* Series inside cluster - responsive grid on mobile, flex on desktop */}
                                            <div className="flex flex-wrap items-start gap-1.5 sm:gap-2">
                                              {Object.entries(cluster.series)
                                                .sort(([a], [b]) => getSeriesPriority(a, cluster.key) - getSeriesPriority(b, cluster.key))
                                                .map(([serieKey, serieGroup], serieIndex) => (
                                                  <div
                                                    key={serieKey}
                                                    className="inline-flex flex-col p-1.5 sm:p-2 bg-white rounded-lg"
                                                  >
                                                    {/* Serie Header (Number) */}
                                                    <div className="flex items-center justify-between gap-1 mb-1">
                                                      <span
                                                        className="px-1.5 sm:px-2 py-0.5 flex items-center justify-center text-[8px] sm:text-[9px] font-semibold rounded-full bg-gray-600 text-white"
                                                      >
                                                        {clusterIndex + 1}.{serieIndex + 1}
                                                      </span>
                                                      <span className="flex items-center gap-1 text-[8px] sm:text-[9px] font-semibold">
                                                        <span style={{ color: themeColors.text }}>({serieKey})</span>
                                                        <span
                                                          className="px-1 py-0.5 rounded"
                                                          style={{ backgroundColor: themeColors.bg, color: themeColors.text }}
                                                        >
                                                          {serieGroup.count}
                                                        </span>
                                                      </span>
                                                    </div>
                                                    {/* Products in serie - 2 columns on mobile */}
                                                    <div className="grid grid-cols-2 gap-1 sm:flex sm:flex-wrap">
                                                      {serieGroup.products.map((product, idx) => (
                                                        <ProductCard
                                                          key={product.id || idx}
                                                          product={product}
                                                          themeColors={themeColors}
                                                        />
                                                      ))}
                                                    </div>
                                                  </div>
                                                ))}
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </AccordionItem>
                              );
                            })}
                      </AccordionItem>
                    );
                  })}
            </AccordionItem>
          ))}
      </div>

      {/* No Results - Responsive */}
      {Object.keys(hierarchy).length === 0 && searchTerm && (
        <div className="py-8 text-center bg-white rounded-lg shadow sm:py-12">
          <svg
            className="w-12 h-12 mx-auto mb-3 text-gray-300 sm:w-16 sm:h-16 sm:mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <p className="px-4 text-sm text-gray-500 sm:text-lg">No products found for "{searchTerm}"</p>
          <button
            onClick={() => setSearchTerm("")}
            className="px-3 py-1.5 mt-3 text-xs transition-colors rounded-lg sm:px-4 sm:py-2 sm:mt-4 sm:text-sm active:scale-95"
            style={{ backgroundColor: themeColors.bg, color: themeColors.text }}
          >
            Clear search
          </button>
        </div>
      )}
    </div>
  );
}

export default FolderView;
